'use client';

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Plus, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function displayUser(user: string): string {
  return user || 'Unknown';
}

export function UserMultiSelect({
  label,
  users,
  selectedUsers,
  onChange,
  allowAll = false,
  minimumSelected = 0,
  allLabel = 'All users',
}: {
  label: string;
  users: string[];
  selectedUsers: string[] | null;
  onChange: (users: string[] | null) => void;
  allowAll?: boolean;
  minimumSelected?: number;
  allLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const options = useMemo(() => {
    const available = new Set(users);
    for (const user of selectedUsers ?? []) available.add(user);
    return Array.from(available).sort((a, b) => a.localeCompare(b));
  }, [selectedUsers, users]);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredOptions = options.filter((user) => (
    displayUser(user).toLocaleLowerCase().includes(normalizedSearch)
  ));
  const selectionLabel = selectedUsers === null
    ? allLabel
    : selectedUsers.length === 0
      ? 'Choose users'
      : selectedUsers.length === 1
        ? displayUser(selectedUsers[0])
        : `${displayUser(selectedUsers[0])} +${selectedUsers.length - 1}`;

  const toggleUser = (user: string) => {
    const current = selectedUsers ?? [];
    if (current.includes(user)) {
      if (current.length <= minimumSelected) return;
      onChange(current.filter((candidate) => candidate !== user));
      return;
    }
    onChange([...current, user]);
  };
  const allFilteredSelected = selectedUsers !== null
    && filteredOptions.every((user) => selectedUsers.includes(user));
  const cannotDeselectAllFiltered = allFilteredSelected
    && (selectedUsers?.length ?? 0) - filteredOptions.length < minimumSelected;
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const filtered = new Set(filteredOptions);
      onChange((selectedUsers ?? []).filter((user) => !filtered.has(user)));
      return;
    }
    const next = new Set(selectedUsers ?? []);
    for (const user of filteredOptions) next.add(user);
    onChange(options.filter((user) => next.has(user)));
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch('');
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={label}
          className="h-7 max-w-[32rem] justify-between gap-2 px-2 text-xs font-normal"
        >
          <span className="text-left whitespace-nowrap">{selectionLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-max min-w-80 max-w-none">
        <div className="relative p-1" onKeyDown={(event) => event.stopPropagation()}>
          <Search className="pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            autoFocus
            aria-label={`${label} search`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search users…"
            className="h-8 w-full rounded-md border border-input bg-background pr-2 pl-8 text-xs outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <DropdownMenuSeparator />
        {allowAll && normalizedSearch.length === 0 && (
          <>
            <DropdownMenuItem
              aria-label={`Use ${allLabel}`}
              onSelect={(event) => {
                event.preventDefault();
                onChange(null);
              }}
            >
              <span className="flex-1 whitespace-nowrap">{allLabel}</span>
              {selectedUsers === null && <Check className="h-4 w-4" aria-hidden="true" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {filteredOptions.length > 0 && (
          <>
            <DropdownMenuItem
              disabled={cannotDeselectAllFiltered}
              aria-label={`${allFilteredSelected ? 'Deselect' : 'Select'} all ${filteredOptions.length} matching users`}
              onSelect={(event) => {
                event.preventDefault();
                toggleAllFiltered();
              }}
            >
              {allFilteredSelected
                ? <X className="h-4 w-4" aria-hidden="true" />
                : <Check className="h-4 w-4" aria-hidden="true" />}
              <span className="flex-1">
                {allFilteredSelected ? 'Deselect all results' : 'Select all results'}
              </span>
              <span className="text-xs text-muted-foreground">{filteredOptions.length}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <div className="max-h-64 overflow-y-auto">
          {filteredOptions.map((user) => {
            const selected = selectedUsers?.includes(user) ?? false;
            const cannotRemove = selected && (selectedUsers?.length ?? 0) <= minimumSelected;
            return (
              <DropdownMenuItem
                key={user}
                disabled={cannotRemove}
                aria-label={`${selected ? 'Remove' : 'Add'} ${displayUser(user)}`}
                onSelect={(event) => {
                  event.preventDefault();
                  toggleUser(user);
                }}
              >
                {!selected && <Plus className="h-4 w-4" aria-hidden="true" />}
                <span className="flex-1 whitespace-nowrap">
                  {displayUser(user)}
                  {!users.includes(user) && ' (no data in range)'}
                </span>
                {selected && <X className="h-4 w-4" aria-hidden="true" />}
              </DropdownMenuItem>
            );
          })}
          {filteredOptions.length === 0 && (
            <div className="px-2 py-5 text-center text-xs text-muted-foreground">No matching users</div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
